/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import agentAwareUtils from 'utils/AgentAware';

import { Flex, Button } from 'components/ui';
import CheckoutButton from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/CheckoutButton';
import PayPalButton from 'components/PayPalButton/PayPalButton';
import PayPalCheckout from 'utils/PayPalCheckout';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import ApplePay from 'services/ApplePay';
import GooglePayButton from 'components/GooglePayButton/GooglePayButton';
import GooglePay from 'utils/GooglePay';
import VenmoExpressButton from 'components/VenmoExpressButton/VenmoExpressButton';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import ButtonGridEN from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/locales/ButtonGrid_en_US';
import ButtonGridFR from 'components/RwdBasket/RwdBasketLayout/CostSummary/Buttons/locales/ButtonGrid_fr_CA';

import { lineHeights, colors, space } from 'style/config';

const { getLocalisedDataFromResource } = localeUtils;

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[3]
    },
    alternateRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: space[3],
        width: '100%'
    },
    button: {
        display: 'flex',
        flex: 1
    },
    checkoutButton: {
        lineHeight: lineHeights.tight,
        display: 'flex',
        width: '100%'
    },
    paypalLoadingButtonBase: {
        width: '100%',
        backgroundColor: colors.paypalYellow,
        transition: 'opacity 0.3s ease-in-out',
        ':disabled': {
            backgroundColor: colors.paypalYellow,
            borderColor: colors.paypalYellow,
            color: colors.white,
            cursor: 'not-allowed'
        },
        '& > div': {
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderTop: `2px solid ${colors.white}`
        }
    },
    paypalContainerBase: {
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        transition: 'opacity 0.3s ease-in-out',
        // PayPal creates its own button and a div wrapper around
        // the iframe. This div has a min-width of 150px, which makes it
        // overlap other buttons in small screens
        '& > div': {
            minWidth: '0 !important'
        }
    },
    visible: {
        opacity: 1,
        pointerEvents: 'auto'
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    },
    loadingButton: {
        position: 'relative',
        display: 'flex',
        flex: 1
    }
};

class ButtonGrid extends BaseClass {
    static counter = 0;
    static MAX_BUTTONS = 3;

    constructor(props) {
        super(props);

        this.state = {
            isApplePayPayment: false,
            isGooglePayPayment: false,
            isPayPalLoading: true
        };
        // One button grid is rendered for desktop and another for mobile,
        // so we have to generate a unique container ID for PayPal.Buttons
        const counterId = ButtonGrid.counter++;
        this.paypalContainerId = `paypal-checkout-container-${String(counterId).padStart(2, '0')}`;
    }

    async componentDidMount() {
        this.isComponentMounted = true;

        this.props.getApplePaymentStatus().then(isApplePayPayment => this.setState({ isApplePayPayment }));
        this.props.getGooglePaymentStatus?.().then(isGooglePayPayment => this.setState({ isGooglePayPayment }));

        // Don't render PayPal button if hideCheckoutButton is true (component returns null)
        if (this.props.hideCheckoutButton) {
            return;
        }

        if (PayPalCheckout.isPayPalCheckoutEnabledForLocale() && this.props.isPaypalPayment) {
            await this.renderPayPalCheckoutButton();
        }
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
    }

    renderPayPalCheckoutButton = async () => {
        const { isBopis } = this.props;

        try {
            await PayPalCheckout.preparePaypalCheckout();
            await PayPalCheckout.renderPayPalButton({ containerId: this.paypalContainerId, isBopis });

            if (this.isComponentMounted) {
                this.setState({ isPayPalLoading: false });
            }
        } catch (error) {
            Sephora.logger.error('[PayPalCheckout] Error rendering PayPal button:', error);

            if (this.isComponentMounted) {
                this.setState({ isPayPalLoading: false });
            }
        }
    };

    componentDidUpdate() {
        this.props.getApplePaymentStatus().then(isApplePayPayment => {
            if (isApplePayPayment !== this.state.isApplePayPayment) {
                this.setState({ isApplePayPayment });
            }
        });
        this.props.getGooglePaymentStatus?.().then(isGooglePayPayment => {
            if (isGooglePayPayment !== this.state.isGooglePayPayment) {
                this.setState({ isGooglePayPayment });
            }
        });
    }

    render() {
        const {
            // Fall back to basketUtils.isPickup() when prop is omitted so the BOPIS-only killswitch
            // does not silently route BOPIS orders to the legacy PayPal flow.
            isBopis = basketUtils.isPickup(),
            isPaypalPayment,
            isVenmoEligible,
            isCheckoutDisabled,
            isSignedIn,
            isSDDBasketAvailable,
            isSDUOnlyInBasket,
            isRougeRewardsApplied,
            hideCheckoutButton,
            transactionAmount
        } = this.props;

        const { isApplePayPayment, isGooglePayPayment, isPayPalLoading } = this.state;
        const hasSDDItem = isSDDBasketAvailable && !isSDUOnlyInBasket;

        // Get localized text for accessibility
        const getText = getLocalisedDataFromResource(ButtonGridEN, ButtonGridFR);

        const {
            container, alternateRow, button, checkoutButton, paypalLoadingButtonBase, paypalContainerBase, visible, hidden, loadingButton
        } =
            styles;

        if (hideCheckoutButton) {
            return null;
        }

        const isApplePayAvailable = Boolean(isApplePayPayment) && isApplePayPayment !== ApplePay.TYPES.HIDDEN;
        const isGooglePayAvailable = Boolean(isGooglePayPayment) && isGooglePayPayment !== GooglePay.TYPES.HIDDEN;
        const payPalCheckoutEnabled = PayPalCheckout.isPayPalCheckoutEnabledForLocale();

        const googlePayButton = (
            <GooglePayButton
                className={agentAwareUtils.applyHideAgentAwareClass()}
                key='gpay'
                isGooglePayPayment={isGooglePayPayment}
                transactionAmount={transactionAmount}
                css={button}
            />
        );

        const applePayButton = (
            <ApplePayButton
                key='applepay'
                isBopis={isBopis}
                isApplePayPayment={isApplePayPayment}
                iconOnly
                css={button}
            />
        );

        const venmoButton = (
            <VenmoExpressButton
                key='venmo'
                className={agentAwareUtils.applyHideAgentAwareClass()}
                isBopis={isBopis}
                isVenmoEligible={isVenmoEligible}
                css={button}
            />
        );

        const payPalButton = payPalCheckoutEnabled ? (
            <Flex
                key='paypal'
                css={loadingButton}
            >
                <Button
                    variant='primary'
                    loading={true}
                    disabled={true}
                    tabIndex={-1}
                    aria-busy={isPayPalLoading}
                    aria-label={getText('paypalLoading')}
                    aria-hidden={!isPayPalLoading}
                    css={[paypalLoadingButtonBase, isPayPalLoading ? visible : hidden]}
                />
                <section
                    id={this.paypalContainerId}
                    aria-label={getText('paypalButton')}
                    aria-hidden={isPayPalLoading}
                    css={[paypalContainerBase, isPayPalLoading ? hidden : visible]}
                />
            </Flex>
        ) : (
            <PayPalButton
                key='paypal'
                className={agentAwareUtils.applyHideAgentAwareClass()}
                isBopis={isBopis}
                isPaypalPayment={isPaypalPayment}
                css={button}
            />
        );

        // Priority is conditional on Apple Pay availability, and the row shows at most 3 buttons
        const candidates = isApplePayAvailable
            ? [
                { node: applePayButton, eligible: isApplePayAvailable },
                { node: payPalButton, eligible: isPaypalPayment },
                { node: googlePayButton, eligible: isGooglePayAvailable },
                { node: venmoButton, eligible: isVenmoEligible }
            ]
            : [
                { node: googlePayButton, eligible: isGooglePayAvailable },
                { node: payPalButton, eligible: isPaypalPayment },
                { node: venmoButton, eligible: isVenmoEligible }
            ];

        const alternateButtons = candidates
            .filter(candidate => candidate.eligible)
            .slice(0, ButtonGrid.MAX_BUTTONS)
            .map(candidate => candidate.node);

        return (
            <Flex css={container}>
                <CheckoutButton
                    isBopis={isBopis}
                    isCheckoutDisabled={isCheckoutDisabled}
                    isSignedIn={isSignedIn}
                    hasSDDItem={hasSDDItem}
                    isRougeRewardsApplied={isRougeRewardsApplied}
                    css={checkoutButton}
                />
                {alternateButtons.length > 0 && (
                    <Flex
                        data-at={Sephora.debug.dataAt('basket_alternate_payments_row')}
                        css={alternateRow}
                    >
                        {alternateButtons}
                    </Flex>
                )}
            </Flex>
        );
    }
}

export default wrapComponent(ButtonGrid, 'ButtonGrid', true);
